import Config from '../core/Config.js';
import db from './db/DexieDB.js';

class FeedbackServiceClass {
  constructor() {
    this.enabled = false;
    this.queueKey = 'feedback_queue'; // localStorage fallback when DB unavailable/disabled
  }

  init() {
    try {
      const flag = Config?.FEATURES?.ANALYTICS === true;
      const userPref = (typeof localStorage !== 'undefined' && localStorage.getItem('enableAnalytics') === 'true');
      this.enabled = !!(flag && userPref);
    } catch (_) {
      this.enabled = false;
    }
    return this.enabled;
  }

  isEnabled() { return this.enabled === true; }

  async submitMessageFeedback({ messageId, role, tabId, promptKey = null, vote = 'none', rating = null, comment = '' }) {
    if (!this.init()) {
      // Queue locally so it doesn't get lost; will be flushed if user enables analytics later
      this.enqueue({ type: 'message', payload: { messageId, role, tabId, promptKey, vote, rating, comment, ts: Date.now() } });
      return null;
    }
    try {
      const rec = { messageId, role, tabId, promptKey, vote, rating, comment, ts: Date.now() };
      // Ensure no id field to satisfy ++id key path
      delete rec.id;
      try {
        return await db.message_feedback.add(rec);
      } catch (e1) {
        // Retry with put as a fallback
        return await db.message_feedback.put(rec);
      }
    } catch (_) { 
      // Last resort: enqueue
      this.enqueue({ type: 'message', payload: { messageId, role, tabId, promptKey, vote, rating, comment, ts: Date.now() } });
      return null; 
    }
  }

  async submitAppFeedback({ category = 'ux', text, contact = '', includeDiagnostics = false }) {
    if (!this.init()) {
      this.enqueue({ type: 'app', payload: { category, text, contact, diagnostics: includeDiagnostics ? this._getDiagnostics() : null, ts: Date.now() } });
      return null;
    }
    try {
      const diag = includeDiagnostics ? this._getDiagnostics() : null;
      const rec = { category, text, contact, diagnostics: diag, ts: Date.now() };
      delete rec.id;
      try {
        return await db.app_feedback.add(rec);
      } catch (e1) {
        return await db.app_feedback.put(rec);
      }
    } catch (_) { 
      this.enqueue({ type: 'app', payload: { category, text, contact, diagnostics: includeDiagnostics ? this._getDiagnostics() : null, ts: Date.now() } });
      return null;
    }
  }

  async listRecent({ limit = 50 } = {}) {
    const [msg, app] = await Promise.all([
      db.message_feedback.orderBy('ts').reverse().limit(limit).toArray(),
      db.app_feedback.orderBy('ts').reverse().limit(limit).toArray()
    ]);
    return { message_feedback: msg, app_feedback: app };
  }

  async queryMessageFeedback({ limit = 10, offset = 0, vote = 'all', minRating = 0 } = {}) {
    let rows = await db.message_feedback.orderBy('ts').reverse().toArray();
    if (vote !== 'all') rows = rows.filter(r => (r.vote || 'none') === vote);
    if (minRating > 0) rows = rows.filter(r => (r.rating || 0) >= minRating);
    const total = rows.length;
    return { total, rows: rows.slice(offset, offset + limit) };
  }

  async queryAppFeedback({ limit = 10, offset = 0, category = 'all' } = {}) {
    let rows = await db.app_feedback.orderBy('ts').reverse().toArray();
    if (category !== 'all') rows = rows.filter(r => (r.category || 'other') === category);
    const total = rows.length;
    return { total, rows: rows.slice(offset, offset + limit) };
  }

  async clearAllFeedback() {
    await Promise.all([db.message_feedback.clear(), db.app_feedback.clear()]);
  }

  enqueue(item) {
    try {
      const q = JSON.parse(localStorage.getItem(this.queueKey) || '[]');
      q.push(item);
      localStorage.setItem(this.queueKey, JSON.stringify(q));
    } catch (_) {}
  }

  async flushQueue() {
    if (!this.init()) return 0;
    let count = 0;
    try {
      const q = JSON.parse(localStorage.getItem(this.queueKey) || '[]');
      for (const it of q) {
        if (it?.type === 'message') {
          try { await db.message_feedback.put({ ...it.payload }); count++; } catch (_) {}
        } else if (it?.type === 'app') {
          try { await db.app_feedback.put({ ...it.payload }); count++; } catch (_) {}
        }
      }
      localStorage.removeItem(this.queueKey);
    } catch (_) {}
    return count;
  }

  _getDiagnostics() {
    try {
      return {
        userAgent: navigator.userAgent,
        url: location.href,
        version: Config?.APP_VERSION,
        env: Config?.APP_ENV
      };
    } catch (_) {
      return { userAgent: navigator.userAgent, url: location.href };
    }
  }
}

const FeedbackService = new FeedbackServiceClass();
export default FeedbackService;

if (import.meta.env?.DEV) {
  try { window.FeedbackService = FeedbackService; } catch (_) {}
}
